import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

// ─── Types ────────────────────────────────────────────────────────────────────
interface CompatModel {
  id: string;
  group_name: string;      // e.g. "Bosch GB062 Serisi"
  model_name: string;      // e.g. "GB062-20 KD H"
  barcode: string;         // barcode of the compatible product
  product_name: string;    // spare part / product name
  notes: string;
  dealer_id: string;
  created_at: string;
}

interface ScanResult {
  barcode: string;
  matches: CompatModel[];
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ModelControlPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id || "";
  const barcodeRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<"control" | "manage">("control");

  // ── Model list ──────────────────────────────────────────────────────────────
  const [models, setModels]     = useState<CompatModel[]>([]);
  const [loading, setLoading]   = useState(true);
  const [query, setQuery]       = useState("");
  const [filterGroup, setFilterGroup] = useState("");

  // ── Add/Edit modal ──────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving]     = useState(false);
  const [formErr, setFormErr]   = useState("");
  const emptyForm = { group_name: "", model_name: "", barcode: "", product_name: "", notes: "" };
  const [form, setForm]         = useState(emptyForm);

  // ── Delete ──────────────────────────────────────────────────────────────────
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Scan / Control ──────────────────────────────────────────────────────────
  const [scanInput, setScanInput] = useState("");
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scanning, setScanning]   = useState(false);

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase
        .from("warehouse_compat_models")
        .select("*")
        .eq("dealer_id", dealerId)
        .order("group_name")
        .order("model_name");
      setModels((data as CompatModel[]) || []);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => { load(); }, [load]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const groups = [...new Set(models.map((m) => m.group_name))].sort();

  const filtered = models.filter((m) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || [m.group_name, m.model_name, m.barcode, m.product_name]
      .some((v) => v?.toLowerCase().includes(q));
    const matchG = !filterGroup || m.group_name === filterGroup;
    return matchQ && matchG;
  });

  // ── Save ───────────────────────────────────────────────────────────────────
  const save = async () => {
    if (!form.group_name.trim()) { setFormErr("Ürün Grubu adı zorunludur!"); return; }
    if (!form.barcode.trim())    { setFormErr("Barkod zorunludur!"); return; }
    setSaving(true); setFormErr("");
    try {
      const supabase = getSupabase();
      const payload = { ...form, dealer_id: dealerId };
      if (editingId) {
        await supabase.from("warehouse_compat_models").update(payload).eq("id", editingId);
      } else {
        await supabase.from("warehouse_compat_models").insert(payload);
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e: any) {
      setFormErr(e.message || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const doDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    const supabase = getSupabase();
    await supabase.from("warehouse_compat_models").delete().eq("id", deleteId);
    setDeleteId(null);
    setDeleting(false);
    load();
  };

  // ── Scan ───────────────────────────────────────────────────────────────────
  const doScan = (val?: string) => {
    const barcode = (val ?? scanInput).trim();
    if (!barcode) return;
    setScanning(true);
    const matches = models.filter((m) =>
      m.barcode.trim().toLowerCase() === barcode.toLowerCase()
    );
    setScanResult({ barcode, matches });
    setScanning(false);
    setScanInput("");
    setTimeout(() => barcodeRef.current?.focus(), 100);
  };

  const handleScanKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") doScan();
  };

  const inputClass = "w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">Model Kontrol</h4>
          <p className="text-sm text-foreground-500 mt-0.5">Ürün barkodunu okutarak uyumlu ürün gruplarını sorgulayın</p>
        </div>
        {activeTab === "manage" && (
          <button
            onClick={() => { setEditingId(null); setForm(emptyForm); setFormErr(""); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium cursor-pointer transition-colors"
          >
            <i className="ri-add-line"></i> Yeni Model Tanımı Ekle
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-background-100 p-1 rounded-xl">
        {[
          { id: "control" as const, label: "Model Kontrol", icon: "ri-barcode-line" },
          { id: "manage"  as const, label: "Model Tanımları Yönet", icon: "ri-list-settings-line" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
              activeTab === tab.id
                ? "bg-background-50 text-foreground-950 shadow-sm"
                : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className={tab.icon}></i> {tab.label}
          </button>
        ))}
      </div>

      {/* ── CONTROL TAB ── */}
      {activeTab === "control" && (
        <div className="space-y-4">
          {/* Scan input */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
            <p className="text-sm font-semibold text-foreground-950 mb-3">
              <i className="ri-barcode-line mr-1.5"></i>Barkod Okut veya Yaz
            </p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  ref={barcodeRef}
                  type="text"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={handleScanKey}
                  autoFocus
                  placeholder="Barkod okuyucuyla okutun veya manuel girin, Enter'a basın..."
                  className="w-full pl-10 pr-4 py-3 bg-background-100 border border-background-200/70 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <button
                onClick={() => doScan()}
                disabled={!scanInput.trim() || scanning}
                className="px-5 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold cursor-pointer disabled:opacity-50 transition-colors"
              >
                <i className="ri-search-line mr-1"></i>Sorgula
              </button>
            </div>
          </div>

          {/* Result */}
          {scanResult && (
            <div className={`bg-background-50 border rounded-xl overflow-hidden ${scanResult.matches.length > 0 ? "border-emerald-200" : "border-accent-200"}`}>
              {/* Result header */}
              <div className={`px-5 py-3 flex items-center gap-3 ${scanResult.matches.length > 0 ? "bg-emerald-50" : "bg-accent-50"}`}>
                <i className={`text-xl ${scanResult.matches.length > 0 ? "ri-checkbox-circle-fill text-emerald-500" : "ri-close-circle-fill text-accent-500"}`}></i>
                <div>
                  <p className="font-semibold text-foreground-950">
                    Barkod: <span className="font-mono">{scanResult.barcode}</span>
                  </p>
                  <p className={`text-sm ${scanResult.matches.length > 0 ? "text-emerald-700" : "text-accent-700"}`}>
                    {scanResult.matches.length > 0
                      ? `${scanResult.matches.length} uyumlu model kaydı bulundu`
                      : "Bu barkod için kayıtlı model uyumu bulunamadı"}
                  </p>
                </div>
                <button
                  onClick={() => setScanResult(null)}
                  className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-200/70 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {scanResult.matches.length > 0 && (
                <div>
                  {/* Group by group_name */}
                  {[...new Set(scanResult.matches.map((m) => m.group_name))].map((grp) => {
                    const grpItems = scanResult.matches.filter((m) => m.group_name === grp);
                    return (
                      <div key={grp} className="border-t border-background-200/70">
                        <div className="px-5 py-2.5 bg-background-100 flex items-center gap-2">
                          <i className="ri-folder-3-line text-primary-500"></i>
                          <span className="font-semibold text-sm text-foreground-950">{grp}</span>
                          <span className="ml-auto text-xs text-foreground-400">{grpItems.length} model</span>
                        </div>
                        <div className="divide-y divide-background-200/70">
                          {grpItems.map((m) => (
                            <div key={m.id} className="px-5 py-3 flex items-start gap-4">
                              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                                <i className="ri-settings-3-line text-primary-600 text-sm"></i>
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-foreground-950">{m.model_name}</p>
                                {m.product_name && <p className="text-xs text-foreground-500 mt-0.5">Ürün: {m.product_name}</p>}
                                {m.notes && <p className="text-xs text-foreground-400 mt-0.5 italic">{m.notes}</p>}
                              </div>
                              <span className="text-xs font-mono text-foreground-400 shrink-0">{m.barcode}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Guide */}
          {!scanResult && (
            <div className="bg-background-100 rounded-xl p-6 text-center">
              <i className="ri-barcode-line text-4xl text-foreground-300 block mb-3"></i>
              <p className="text-sm text-foreground-500 mb-1">Bir ürünün barkodunu okutun</p>
              <p className="text-xs text-foreground-400">Sistemde kayıtlı model tanımlarıyla karşılaştırılarak uyumlu ürün grupları gösterilir</p>
            </div>
          )}
        </div>
      )}

      {/* ── MANAGE TAB ── */}
      {activeTab === "manage" && (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Grup, model, barkod, ürün ara..."
                className="w-full pl-9 pr-4 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none cursor-pointer"
            >
              <option value="">Tüm Gruplar</option>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          {/* Table */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            {loading ? (
              <div className="py-16 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i></div>
            ) : filtered.length === 0 ? (
              <div className="py-14 text-center">
                <i className="ri-list-settings-line text-3xl text-foreground-300 block mb-2"></i>
                <p className="text-sm text-foreground-400">Kayıt bulunamadı</p>
                <p className="text-xs text-foreground-400 mt-1">Yeni Model Tanımı Ekle butonuyla başlayın</p>
              </div>
            ) : (
              <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-320px)]">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-background-100 sticky top-0">
                    <tr>
                      {["Ürün Grubu", "Model Adı", "Barkod", "Ürün Adı", "Not", ""].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-foreground-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((m) => (
                      <tr key={m.id} className="border-t border-background-200/70 hover:bg-background-100/40 transition-colors">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                            <i className="ri-folder-3-line"></i>{m.group_name}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground-950">{m.model_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-foreground-600">{m.barcode}</td>
                        <td className="px-4 py-3 text-foreground-600 max-w-[200px] truncate">{m.product_name || "—"}</td>
                        <td className="px-4 py-3 text-foreground-400 text-xs max-w-[150px] truncate">{m.notes || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingId(m.id);
                                setForm({ group_name: m.group_name, model_name: m.model_name, barcode: m.barcode, product_name: m.product_name, notes: m.notes });
                                setFormErr("");
                                setShowModal(true);
                              }}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-primary-100 hover:text-primary-600 cursor-pointer transition-colors"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setDeleteId(m.id)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-accent-100 hover:text-accent-600 cursor-pointer transition-colors"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <p className="text-xs text-foreground-400 text-right mt-2">{filtered.length} kayıt</p>
        </div>
      )}

      {/* ── Add/Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-lg font-semibold text-foreground-950">
                {editingId ? "Model Tanımını Düzenle" : "Yeni Model Tanımı Ekle"}
              </h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün Grubu *</label>
                <input
                  type="text"
                  value={form.group_name}
                  onChange={(e) => setForm({ ...form, group_name: e.target.value })}
                  placeholder="Örn: Bosch GB062 Serisi"
                  autoFocus
                  list="group-suggestions"
                  className={inputClass}
                />
                <datalist id="group-suggestions">
                  {groups.map((g) => <option key={g} value={g} />)}
                </datalist>
                <p className="text-[11px] text-foreground-400 mt-0.5">Aynı gruba ait birden fazla model ekleyebilirsiniz</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Model Adı</label>
                <input
                  type="text"
                  value={form.model_name}
                  onChange={(e) => setForm({ ...form, model_name: e.target.value })}
                  placeholder="Örn: GB062-20 KD H (Kombi)"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Barkod *</label>
                <input
                  type="text"
                  value={form.barcode}
                  onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                  placeholder="Uyumlu ürünün barkodunu okutun veya girin"
                  className={inputClass}
                />
                <p className="text-[11px] text-foreground-400 mt-0.5">Bu barkod okutulduğunda bu model grubu sorgu sonucunda çıkacak</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Ürün / Parça Adı</label>
                <input
                  type="text"
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  placeholder="Örn: Ateşleme Elektrodu"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Not</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ek bilgi, uyarı vb."
                  rows={2}
                  className={inputClass + " resize-none"}
                />
              </div>
            </div>

            {formErr && (
              <div className="mt-3 p-3 bg-accent-50 border border-accent-200 text-accent-700 rounded-lg text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i> {formErr}
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer hover:bg-background-200/70">İptal</button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50 transition-colors">
                {saving ? <><i className="ri-loader-4-line animate-spin mr-1"></i>Kaydediliyor...</> : <><i className="ri-check-line mr-1"></i>Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteId(null)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl text-accent-600"></i>
            </div>
            <h4 className="text-center font-semibold text-foreground-950 mb-1">Kaydı Sil</h4>
            <p className="text-center text-sm text-foreground-500 mb-5">Bu model tanımı silinecek. Geri alınamaz.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer">İptal</button>
              <button onClick={doDelete} disabled={deleting} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer disabled:opacity-50">
                {deleting ? "Siliniyor..." : "Evet, Sil"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
